import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkScheduleModelStore } from '../../store/useWorkScheduleModelStore.ts';
import { useToastStore } from '../../store/useToastStore.ts';
import { Card } from '../../components/Shared/Card.tsx';
import { Loading } from '../../components/Shared/Loading.tsx';
import { Button } from '../../components/Shared/Button.tsx';
import { Badge } from '../../components/Shared/Badge.tsx';
import { EmptyState } from '../../components/Shared/EmptyState.tsx';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog.tsx';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../components/Shared/Table.tsx';
import { WorkScheduleModelDialog, type WorkScheduleModelFormData } from '../../components/Shared/WorkScheduleModelDialog.tsx';
import { Plus, Clock, Edit, Trash2, Search } from 'lucide-react';
import { formatDate, isoDate } from '../../utils/dateUtils.ts';
import { WorkScheduleModelChangePreviewModal } from './WorkScheduleModelChangePreviewModal.tsx';
import type {
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelDto as WorkScheduleModelDto,
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsUpdateWorkScheduleModelRequest as UpdateWorkScheduleModelRequest,
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelChangePreviewDto as ChangePreviewDto,
} from '../../api/generated/models';

export default function WorkScheduleModelsPage() {
  const { t } = useTranslation(['workScheduleModels', 'common']);
  const {
    models,
    loading,
    
    error,
    fetchModels,
    createModel,
    updateModel,
    previewModelChange,
    deleteModel,
  } = useWorkScheduleModelStore();
  const { addToast } = useToastStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('active');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedModel, setSelectedModel] = useState<WorkScheduleModelDto | null>(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<{ id: string; name: string } | null>(null);
  const [pendingPreview, setPendingPreview] = useState<{
    preview: ChangePreviewDto;
    resolve: (overwriteDeviationsFor: string[] | null) => void;
  } | null>(null);

  useEffect(() => {
    fetchModels(filterStatus === 'all').then(() => setInitialLoadComplete(true));
  }, [fetchModels, filterStatus]);

  // Client-side filtering
  const filteredModels = models.filter((model) => {
    if (searchTerm) {
      const matches = model.name?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matches) return false;
    }
    if (filterStatus === 'active' && !model.isActive) return false;
    if (filterStatus === 'inactive' && model.isActive) return false;
    return true;
  });

  const handleCreate = () => {
    setSelectedModel(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleEdit = async (modelId: string) => {
    const model = models.find(m => m.workScheduleModelId === modelId);
    if (model) {
      // Fetch full details
      const { fetchModelById } = useWorkScheduleModelStore.getState();
      await fetchModelById(modelId);
      const fullModel = useWorkScheduleModelStore.getState().currentModel;
      setSelectedModel(fullModel);
      setDialogMode('edit');
      setDialogOpen(true);
    }
  };

  // Shows the change preview and resolves with the employees whose deviating days are to be
  // overwritten, or null when the user cancels (FAFD-67).
  const askForPreview = async (preview: ChangePreviewDto): Promise<string[] | null> => {
    const choice = await new Promise<string[] | null>((resolve) => setPendingPreview({ preview, resolve }));
    setPendingPreview(null);
    return choice;
  };

  // Errors propagate to the dialog, which owns the error display. Returns false when the user
  // cancels the change preview, so the dialog stays open.
  const handleSave = async (data: WorkScheduleModelFormData): Promise<boolean> => {
    if (dialogMode === 'create') {
      await createModel({
        name: data.name,
        weeklyHours: data.weeklyHours,
        workDays: data.workDays,
      });
      addToast(t('workScheduleModels:messages.created'), 'success');
    } else if (selectedModel) {
      const id = selectedModel.workScheduleModelId!;
      const request: UpdateWorkScheduleModelRequest = {
        name: data.name,
        weeklyHours: data.weeklyHours,
        isActive: data.isActive,
        workDays: data.workDays,
        validFrom: data.validFrom,
      };
      // why: the preview also tells whether anything changes at all — a save that changes nothing
      // must not report "updated" (FAFD-67 customer case).
      const preview = await previewModelChange(id, request);
      const affected =
        (preview.updatedDays ?? 0) +
        (preview.createdDays ?? 0) +
        (preview.removedDays ?? 0) +
        (preview.skippedDays ?? 0) +
        (preview.deviatingDays ?? 0);
      const detailsChanged =
        data.name.trim() !== (selectedModel.name ?? '') ||
        data.weeklyHours !== selectedModel.weeklyHours ||
        data.isActive !== selectedModel.isActive;
      if (!preview.planChanged && affected === 0 && !detailsChanged) {
        addToast(t('workScheduleModels:messages.noChange'), 'info');
        setDialogOpen(false);
        return true;
      }

      let overwrite: string[] = [];
      if (data.validFrom < isoDate(new Date()) && affected > 0) {
        const choice = await askForPreview(preview);
        if (choice === null) {
          return false;
        }
        overwrite = choice;
      }
      await updateModel(id, { ...request, overwriteDeviationsForEmployeeIds: overwrite });
      addToast(t('workScheduleModels:messages.updated'), 'success');
    }
    setDialogOpen(false);
    return true;
  };

  const handleDelete = (modelId: string, modelName: string) => {
    setModelToDelete({ id: modelId, name: modelName });
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!modelToDelete) return;

    try {
      await deleteModel(modelToDelete.id);
      addToast(t('workScheduleModels:messages.deleted'), 'success');
    } catch (error) {
      addToast(
        error instanceof Error ? error.message : t('workScheduleModels:errors.deleteError'),
        'error'
      );
    } finally {
      setIsDeleteDialogOpen(false);
      setModelToDelete(null);
    }
  };

  if (loading && !initialLoadComplete) {
    return <Loading />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('workScheduleModels:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('workScheduleModels:subtitle')}
            </p>
          </div>
          <Button
            onClick={handleCreate}
            leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
            className="w-full sm:w-auto"
          >
            <span className="hidden sm:inline">{t('workScheduleModels:buttons.newModel')}</span>
            <span className="sm:hidden">{t('workScheduleModels:buttons.newModelShort')}</span>
          </Button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
              <input
                type="text"
                placeholder={t('workScheduleModels:search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
              />
            </div>
          </div>
          <select
            id="statusFilter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label={t('common:filters.allStatus')}
            className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
          >
            <option value="all">{t('workScheduleModels:filters.allStatus')}</option>
            <option value="active">{t('workScheduleModels:filters.active')}</option>
            <option value="inactive">{t('workScheduleModels:filters.inactive')}</option>
          </select>
        </div>
      </div>

      {/* Table or Empty State */}
      {error && initialLoadComplete ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('workScheduleModels:errors.loadError')}
            description={error}
            onRetry={() => fetchModels(filterStatus === 'all')}
          />
        </Card>
      ) : filteredModels.length === 0 ? (
        <EmptyState
          icon={<Clock className="w-12 h-12" strokeWidth={1.75} />}
          title={
            searchTerm || filterStatus !== 'active'
              ? t('workScheduleModels:empty.titleFiltered')
              : t('workScheduleModels:empty.title')
          }
          description={
            searchTerm || filterStatus !== 'active'
              ? t('workScheduleModels:empty.descriptionFiltered')
              : t('workScheduleModels:empty.description')
          }
          action={
            !searchTerm && filterStatus === 'active' ? (
              <Button
                onClick={handleCreate}
                leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              >
                {t('workScheduleModels:buttons.createFirst')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card noPadding>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>{t('workScheduleModels:table.name')}</TableHeader>
                <TableHeader className="hidden md:table-cell">{t('workScheduleModels:table.weeklyHours')}</TableHeader>
                <TableHeader className="hidden lg:table-cell">{t('workScheduleModels:table.workDays')}</TableHeader>
                <TableHeader className="hidden lg:table-cell">{t('workScheduleModels:table.validFrom')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">{t('workScheduleModels:table.status')}</TableHeader>
                <TableHeader>{t('workScheduleModels:table.actions')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModels.map((model) => (
                <TableRow key={model.workScheduleModelId}>
                  <TableCell>
                    <div className="font-medium text-[13px] text-slate-900">
                      {model.name}
                    </div>
                    {/* Mobile: condensed info */}
                    <div className="text-[11px] text-slate-500 mt-0.5 space-y-0.5">
                      <div className="md:hidden tabular-nums">
                        {model.weeklyHours}{t('workScheduleModels:table.hoursPerWeek')}
                      </div>
                      <div className="lg:hidden tabular-nums">
                        {model.workingDaysCount} {t('workScheduleModels:table.workDaysCount')}
                      </div>
                      <div className="sm:hidden">
                        <Badge variant={model.isActive ? 'success' : 'default'}>
                          {model.isActive
                            ? t('workScheduleModels:status.active')
                            : t('workScheduleModels:status.inactive')}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="text-[13px] font-medium text-slate-900 tabular-nums">
                      {model.weeklyHours}{t('workScheduleModels:table.hours')}
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="text-[13px] text-slate-600 tabular-nums">
                      {model.workingDaysCount} {t('workScheduleModels:table.days')}
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="text-[13px] text-slate-600 tabular-nums">
                      {model.validFrom
                        ? formatDate(model.validFrom)
                        : t('workScheduleModels:table.sinceBeginning')}
                    </div>
                    {model.hasFutureVersion && (
                      <Badge variant="warning" className="mt-1">
                        {t('workScheduleModels:table.plannedChange')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant={model.isActive ? 'success' : 'default'}>
                      {model.isActive
                        ? t('workScheduleModels:status.active')
                        : t('workScheduleModels:status.inactive')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(model.workScheduleModelId!)}
                        title={t('common:actions.edit')}
                        aria-label={t('common:actions.edit')}
                        className="p-1.5"
                      >
                        <Edit className="w-4 h-4" strokeWidth={2} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleDelete(model.workScheduleModelId!, model.name!)
                        }
                        title={t('common:actions.delete')}
                        aria-label={t('common:actions.delete')}
                        className="p-1.5"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" strokeWidth={2} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <WorkScheduleModelDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        model={selectedModel ?? undefined}
        mode={dialogMode}
      />

      {/* Preview of a change from a past date (FAFD-67) */}
      <WorkScheduleModelChangePreviewModal
        preview={pendingPreview?.preview ?? null}
        onCancel={() => pendingPreview?.resolve(null)}
        onConfirm={(overwriteDeviationsFor) => pendingPreview?.resolve(overwriteDeviationsFor)}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setModelToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('workScheduleModels:confirm.deleteTitle')}
        message={t('workScheduleModels:confirm.deleteMessage', { name: modelToDelete?.name })}
        confirmText={t('workScheduleModels:confirm.deleteButton')}
        cancelText={t('workScheduleModels:confirm.cancelButton')}
        variant="danger"
      />
    </div>
  );
}
